import { Link } from 'react-router-dom'

import { BarChart, Card, Kpi, Loaded, Notice, Page, StackedBar, Table } from '../components/ui'
import { get } from '../lib/api'
import { count, dateTime, money, ms, percent, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Overview = {
  users_total: number
  users_pro: number
  dispatches_24h: number
  ai_spend_cycle_usd: number
  ai_avg_latency_ms: number
  models_with_unverified_prices: string[]
}

type UsageSeries = {
  series: { day: string; cost_usd: number; requests: number }[]
  cycle_spend_usd: number
  cycle_budget_usd: number | null
  projected_cycle_usd: number
  p50_latency_ms: number
  p95_latency_ms: number
  error_taxonomy: Record<string, number>
}

type UsageSummary = {
  total_cost_usd: number
  total_requests: number
  by_provider: Record<
    string,
    { cost_usd: number; requests: number; input_tokens: number; output_tokens: number }
  >
}

type EmergencyEvent = {
  id: string
  user_id: string
  reason: string
  outcome: string
  triggered_at: string
}

type System = {
  environment: string
  database: string
  ai_error_rate_1h: number
  ai_requests_1h: number
}

export function Overview() {
  const overview = useResource((signal) => get<Overview>('/v1/admin/overview', undefined, signal))
  const series = useResource((signal) =>
    get<UsageSeries>('/v1/admin/ai/usage/series', { days: 14 }, signal),
  )
  const summary = useResource((signal) =>
    get<UsageSummary>('/v1/admin/ai/usage', { days: 30 }, signal),
  )
  const events = useResource((signal) =>
    get<EmergencyEvent[]>('/v1/admin/emergency/events', { limit: 8 }, signal),
  )
  const system = useResource((signal) => get<System>('/v1/admin/system', undefined, signal))

  return (
    <Page
      title="Overview"
      subtitle="Everything on this page is live from the API. Nothing is sampled or estimated except where it says so."
    >
      <Loaded resource={overview}>
        {(data) => (
          <>
            {data.models_with_unverified_prices.length > 0 && (
              <Notice tone="warn">
                <strong>{data.models_with_unverified_prices.length} model(s)</strong> in the
                catalog carry prices that have not been confirmed against the provider's
                published rate:{' '}
                <span className="mono">{data.models_with_unverified_prices.join(', ')}</span>.
                Spend figures involving them are estimates.{' '}
                <Link to="/ai/models" style={{ textDecoration: 'underline' }}>
                  Confirm prices →
                </Link>
              </Notice>
            )}

            <div className="grid grid--kpi">
              <Kpi label="Users" value={count(data.users_total)} foot={<Link to="/users">All users →</Link>} />
              <Kpi
                label="Pro subscribers"
                value={count(data.users_pro)}
                tone="lime"
                foot={
                  <>
                    {percent(data.users_total ? data.users_pro / data.users_total : 0)} conversion ·{' '}
                    <Link to="/billing">Billing →</Link>
                  </>
                }
              />
              <Kpi
                label="Dispatches · 24 h"
                value={count(data.dispatches_24h)}
                tone={data.dispatches_24h > 0 ? 'alert' : undefined}
                foot={<Link to="/emergency">Event log →</Link>}
              />
              <Kpi
                label="AI spend · cycle"
                value={money(data.ai_spend_cycle_usd)}
                tone="green"
                foot={<Link to="/ai/usage">Usage console →</Link>}
              />
              <Kpi
                label="Avg latency · 24 h"
                value={ms(data.ai_avg_latency_ms)}
                foot={
                  series.data
                    ? `p95 ${ms(series.data.p95_latency_ms)}`
                    : 'first-token, gateway-measured'
                }
              />
              <Kpi
                label="AI error rate · 1 h"
                value={system.data ? percent(system.data.ai_error_rate_1h) : '—'}
                tone={system.data && system.data.ai_error_rate_1h > 0.05 ? 'alert' : undefined}
                foot={<Link to="/system">System →</Link>}
              />
            </div>
          </>
        )}
      </Loaded>

      <div className="grid grid--2">
        <Card title="AI spend · last 14 days" hint="Daily total across every provider">
          <Loaded resource={series}>
            {(data) => (
              <BarChart
                data={data.series.map((point) => ({
                  label: point.day.slice(5),
                  value: point.cost_usd,
                }))}
                format={(v) => money(v)}
              />
            )}
          </Loaded>
        </Card>

        <Card title="Spend by provider · 30 days" hint="Where the money actually goes">
          <Loaded resource={summary}>
            {(data) => (
              <div className="stack">
                <StackedBar
                  segments={Object.entries(data.by_provider).map(([provider, bucket]) => ({
                    label: titleCase(provider),
                    value: bucket.cost_usd,
                  }))}
                  format={(v) => money(v)}
                />
                <div className="row" style={{ gap: 20, fontSize: 12 }}>
                  <span className="muted">
                    Total <span className="num">{money(data.total_cost_usd)}</span>
                  </span>
                  <span className="muted">
                    Requests <span className="num">{count(data.total_requests)}</span>
                  </span>
                </div>
              </div>
            )}
          </Loaded>
        </Card>
      </div>

      <div className="grid grid--2">
        <Card
          title="Recent emergency events"
          hint="Vitals are withheld here — open an event to see them, which is audited"
          action={<Link className="btn btn--sm btn--ghost" to="/emergency">All events</Link>}
          flush
        >
          <Loaded resource={events}>
            {(rows) => (
              <Table
                rows={rows}
                rowKey={(row) => row.id}
                empty="No dispatches recorded. That is the good outcome."
                columns={[
                  {
                    key: 'reason',
                    header: 'Reason',
                    strong: true,
                    render: (row) => titleCase(row.reason),
                  },
                  {
                    key: 'outcome',
                    header: 'Outcome',
                    render: (row) => (
                      <span
                        className={
                          row.outcome === 'cancelled' ? 'badge' : 'badge badge--alert'
                        }
                      >
                        {titleCase(row.outcome)}
                      </span>
                    ),
                  },
                  {
                    key: 'at',
                    header: 'Triggered',
                    render: (row) => <span className="dim">{dateTime(row.triggered_at)}</span>,
                  },
                ]}
              />
            )}
          </Loaded>
        </Card>

        <Card title="Error taxonomy · 14 days" hint="Gateway failures grouped by class">
          <Loaded resource={series}>
            {(data) =>
              Object.keys(data.error_taxonomy).length === 0 ? (
                <div className="empty">No gateway errors in the window.</div>
              ) : (
                <BarChart
                  color="var(--alert-bright)"
                  data={Object.entries(data.error_taxonomy).map(([kind, n]) => ({
                    label: kind,
                    value: n,
                  }))}
                />
              )
            }
          </Loaded>
        </Card>
      </div>
    </Page>
  )
}
